import {DestroyRef, inject} from '@angular/core';
import {Socket} from 'socket.io-client';
import {environment} from '../../environments/environment';
import {SocketIoFactory} from './socket-io-factory.service';

/**
 * Gemeinsame WebSocket-Verbindungslogik für AuthStatusService und EmployeeSyncStatusService:
 * beide bauten unabhängig voneinander (fast wortgleich) eine socket.io-Verbindung mit eigener
 * Reconnect-/Backoff-Logik und einem Polling-Fallback auf, falls WebSockets nicht verfügbar
 * sind (z.B. Proxy/Firewall blockiert Upgrade-Requests).
 *
 * Subklassen implementieren nur das, was wirklich servicespezifisch ist: die WS-URL, den
 * Event-Namen/Handler des Push-Events sowie die Polling-Fetch-Funktion samt Intervall. Die
 * Verbindungs-/Reconnect-/Polling-Mechanik selbst lebt einmalig hier.
 */
export abstract class ResilientSocketConnection<TEventData = unknown> {
    protected socket: Socket | null = null;
    private wsAvailable = false;
    private reconnectAttempts = 0;
    private readonly maxReconnectAttempts = 5;
    private reconnectTimeoutId: any = null;
    private pollIntervalId: any = null;
    private readonly destroyRef = inject(DestroyRef);

    protected constructor(private readonly socketFactory: SocketIoFactory) {
    }

    /** Liefert die zu verbindende WebSocket-URL (fester Env-Wert oder aus window.location abgeleitet). */
    protected abstract getWsUrl(): string;

    /** Name des servicespezifischen Push-Events (z.B. 'auth-status', 'sync-status'). */
    protected abstract getEventName(): string;

    /** Verarbeitet eine über das Push-Event empfangene Nutzlast. */
    protected abstract handleEvent(data: TEventData): void;

    /** Holt den aktuellen Status per HTTP (Polling-Fallback), wird auch bei jedem Intervall-Tick aufgerufen. */
    protected abstract poll(): void | Promise<void>;

    /** Intervall zwischen zwei Polling-Fetches in ms. */
    protected abstract getPollIntervalMs(): number;

    /**
     * Wird genau einmal aufgerufen, wenn das Polling tatsächlich startet (initial via
     * environment.useMocks oder als WebSocket-Fallback). Default: no-op. Subklassen können hier
     * z.B. einen sofortigen ersten Fetch zusätzlich zum Intervall auslösen - das Polling-Intervall
     * wird erst gestartet, nachdem ein zurückgegebenes Promise aufgelöst ist (wichtig, falls der
     * sofortige Fetch selbst schon stopPolling() auslösen kann, z.B. weil er "nicht authentifiziert"
     * ergibt).
     */
    protected onPollingStarted(): void | Promise<void> {
    }

    /** Baut die Verbindung auf (WebSocket oder, falls useMocks, direkt Polling) und registriert cleanup(). */
    protected initConnection(): void {
        if (environment.useMocks) {
            this.startPolling();
        } else {
            this.connectWebSocket();
        }

        this.destroyRef.onDestroy(() => {
            this.cleanup();
        });
    }

    private connectWebSocket(): void {
        try {
            const wsUrl = this.getWsUrl();
            const token = this.getCookie('accessToken');
            const refreshToken = this.getCookie('refreshToken');

            this.socket = this.socketFactory.create(wsUrl, {
                auth: {token, refreshToken},
                // Backend validiert die Verbindung zusätzlich gegen die echte
                // Express-Session (httpOnly sessionId-Cookie) - ohne withCredentials
                // würde der Browser dieses Cookie beim cross-origin Handshake
                // (Port 8080 statt 4200) gar nicht mitschicken.
                withCredentials: true,
                // eigene Reconnect-/Backoff-Logik statt der eingebauten von socket.io,
                // damit der bestehende Polling-Fallback nach maxReconnectAttempts greift.
                reconnection: false,
                // Hinter Traefik ist nur /api zum Backend geroutet (stripprefix entfernt das
                // Präfix wieder) - ohne diesen Pfad würde der Socket.IO-Handshake (immer auf dem
                // Standardpfad /socket.io, unabhängig vom Namespace) beim Frontend-Container statt
                // beim Backend landen. In der lokalen Dev-Umgebung (Direktverbindung zum
                // Backend-Port, kein Proxy) bleibt wsPath leer, also der Standardpfad.
                path: environment.wsPath || undefined,
            });

            this.socket.on('connect', () => {
                this.wsAvailable = true;
                this.reconnectAttempts = 0;
                this.stopPolling();
            });

            this.socket.on(this.getEventName(), (data: TEventData) => {
                this.handleEvent(data);
            });

            this.socket.on('connect_error', () => {
                if (!this.wsAvailable) {
                    // Nie erfolgreich verbunden — WebSocket nicht verfügbar, Polling nutzen
                    this.socket = null;
                    this.startPolling();
                }
            });

            this.socket.on('disconnect', () => {
                this.socket = null;
                if (!this.wsAvailable) {
                    this.startPolling();
                } else {
                    this.scheduleReconnect();
                }
            });
        } catch {
            this.startPolling();
        }
    }

    private scheduleReconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.wsAvailable = false;
            this.startPolling();
            return;
        }

        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectAttempts++;

        this.reconnectTimeoutId = setTimeout(() => {
            this.connectWebSocket();
        }, delay);
    }

    protected startPolling(): void {
        if (this.pollIntervalId) return;

        const started = this.onPollingStarted();
        const armInterval = () => {
            this.pollIntervalId = setInterval(() => {
                void this.poll();
            }, this.getPollIntervalMs());
        };

        if (started && typeof (started as Promise<void>).then === 'function') {
            (started as Promise<void>).then(armInterval);
        } else {
            armInterval();
        }
    }

    protected stopPolling(): void {
        if (this.pollIntervalId) {
            clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
        }
    }

    private cleanup(): void {
        this.stopPolling();
        clearTimeout(this.reconnectTimeoutId);
        if (this.socket) {
            this.wsAvailable = false;
            this.socket.close();
            this.socket = null;
        }
    }

    private getCookie(name: string): string {
        const cookieArray: Array<string> = document.cookie.split(';');
        const cookieName = `${name}=`;

        for (let cookie of cookieArray) {
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    }
}
